<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToTable'
});
</script>
<template>
	<div ref="$el" :key="langKey" :flex="flex ? flex : null" class="to-table" :class="setClass" :style="setStyle">
		<div ref="body" class="to-table-wrapper" @scroll="scrollHandle">
			<table ref="bodyTable" class="to-table-table">
				<colgroup>
					<col v-for="(item, index) in columns" :key="index" :class="item.type ? `type-${item.type}` : ''" class="to-table-col" :style="{ width: getColWidth(item) }" />
				</colgroup>
				<TableHead ref="bodyHead" :checked-all="checkedAll" :data="columns" @change="checkedAllChange" @sort="sortChange"></TableHead>
				<TableBody ref="bodyBody" :type="hasRadio ? 'radio' : hasCheck ? 'check' : ''" :data="data" :checked-all="checkedAll" :column-data="columns" @change="change">
					<template v-for="columnItem in flattenColumns" #[columnItem.prop]="slotProps">
						<slot :name="columnItem.prop" v-bind="slotProps" />
					</template>
					<template #detail="slotProps">
						<slot name="detail" v-bind="slotProps" />
					</template>
				</TableBody>
			</table>
			<div v-if="!data.length" class="to-table-empty">{{ props.emptyText || t('to.table.emptyText') }}</div>
		</div>
		<div v-if="Number(fixTop) > 0" ref="top" class="to-table-top">
			<table ref="topTable" class="to-table-table">
				<TableHead ref="topHead" :data="columns" @change="checkedAllChange" @sort="sortChange"></TableHead>
			</table>
		</div>
		<div v-if="Number(fixLeft) > 0" ref="left" class="to-table-left">
			<table ref="leftTable" class="to-table-table">
				<TableHead ref="leftHead" :data="columns.slice(0, Number(props.fixLeft))" @change="checkedAllChange" @sort="sortChange"></TableHead>
				<TableBody ref="leftBody" :data="data" :column-data="columns.slice(0, Number(props.fixLeft))" @change="change">
					<template v-for="columnItem in flattenColumns" #[columnItem.prop]="slotProps">
						<slot :name="columnItem.prop" v-bind="slotProps" />
					</template>
					<template #detail="slotProps">
						<slot name="detail" v-bind="slotProps" />
					</template>
				</TableBody>
			</table>
		</div>
		<div v-if="Number(fixLeft) > 0 && Number(fixTop) > 0" ref="leftTop" class="to-table-left-top">
			<table ref="leftTopTable" class="to-table-table">
				<TableHead ref="leftTopHead" :data="columns.slice(0, Number(props.fixLeft))" @change="checkedAllChange" @sort="sortChange"></TableHead>
			</table>
		</div>
		<div v-if="Number(fixRight) > 0" ref="right" class="to-table-right">
			<table ref="rightTable" class="to-table-table">
				<TableHead ref="rightHead" :data="columns.slice(columns.length - Number(props.fixRight), columns.length)" @change="checkedAllChange" @sort="sortChange"></TableHead>
				<TableBody ref="rightBody" :data="data" :column-data="columns.slice(columns.length - Number(fixRight), columns.length)" @change="change">
					<template v-for="columnItem in flattenColumns" #[columnItem.prop]="slotProps">
						<slot :name="columnItem.prop" v-bind="slotProps" />
					</template>
					<template #detail="slotProps">
						<slot name="detail" v-bind="slotProps" />
					</template>
				</TableBody>
			</table>
		</div>
		<div v-if="Number(fixRight) > 0 && Number(fixTop) > 0" ref="rightTop" class="to-table-right-top">
			<table ref="rightTopTable" class="to-table-table">
				<TableHead ref="rightTopHead" :data="columns.slice(columns.length - Number(props.fixRight), columns.length)" @change="checkedAllChange" @sort="sortChange"></TableHead>
			</table>
		</div>
	</div>
</template>

<script setup lang="ts">
import TableHead from './table-head.vue'
import TableBody from './table-body.vue'
import { computed, onMounted, ref, watch, nextTick, provide } from 'vue'
import { t, langKey } from '@touchvue/locale'

const emit = defineEmits(['change', 'update:modelValue', 'sort', 'detailChange'])
export type modelType = any[] | string | number
// 定义参数
export interface Props {
	flex?: boolean
	columns?: { prop?: string; label?: string; type?: string; toggler?: boolean; _show: boolean; width?: number | string; wrap?: boolean }[]
	data?: Record<string, any>[]
	height?: number | string
	fixTop?: number | string
	fixLeft?: number | string
	fixRight?: number | string
	mode?: string
	wrap?: boolean
	thWrap?: boolean
	emptyText?: string
	modelValue?: modelType
	valueKeys?: string | number
	sortFunction?: ((column, type) => any) | null
}
const props = withDefaults(defineProps<Props>(), {
	mode: 'default',
	columns: () => [],
	data: () => [],
	height: '',
	fixLeft: 0,
	fixRight: 0,
	fixTop: 0,
	wrap: false,
	thWrap: false,
	emptyText: '',
	modelValue: '',
	valueKeys: '_id',
	flex: false,
	sortFunction: null
})

// 获取 ref
const $el = ref(null)
const top = ref(null)
const topTable = ref(null)
const topHead = ref(null)
const body = ref(null)
const bodyTable = ref(null)
const bodyHead = ref(null)
const bodyBody = ref(null)
const left = ref(null)
const leftTable = ref(null)
const leftHead = ref(null)
const leftBody = ref(null)
const leftTop = ref(null)
const leftTopTable = ref(null)
const leftTopHead = ref(null)
const right = ref(null)
const rightTable = ref(null)
const rightHead = ref(null)
const rightBody = ref(null)
const rightTop = ref(null)
const rightTopTable = ref(null)
const rightTopHead = ref(null)

// 定义变量
const checkedAll = ref(false)

// 设置样式
const setClass = computed(() => {
	const classes: string[] = []
	if (props.height === 'full') classes.push('height-full')
	if (props.mode) classes.push(`mode-${props.mode}`)
	if (props.wrap) classes.push('is-wrap')
	if (isSetWidth()) {
		classes.push('is-fixed')
	}
	return classes
})
const setStyle = computed(() => {
	const obj: Record<string, string> = {}

	if (props.height && props.height !== 'full') {
		obj.height = isNaN(Number(props.height as string)) ? String(props.height) : `${props.height}em`
	}

	return obj
})

watch(
	() => props.modelValue,
	val => {
		nextTick(() => {
			bodyBody.value.setCheckData(val, props.valueKeys)
			if (Number(props.fixLeft) > 0) {
				leftBody.value.setCheckData(val, props.valueKeys)
			}
			if (Number(props.fixRight) > 0) {
				rightBody.value.setCheckData(val, props.valueKeys)
			}
		})
	},
	{ immediate: true, deep: true }
)

function isSetWidth() {
	return props.columns.some(item => item.width)
}

// 固定表头/表身
function getBarWidth() {
	var scrollTester = document.createElement('div')
	scrollTester.id = 'scrollTester'
	scrollTester.style.width = '200px'
	scrollTester.style.height = '200px'
	scrollTester.style.position = 'absolute'
	scrollTester.style.visibility = 'hidden'
	scrollTester.style.overflow = 'scroll'
	document.body.appendChild(scrollTester)
	var barWidth = document.getElementById('scrollTester').offsetWidth - document.getElementById('scrollTester').scrollWidth
	document.body.removeChild(scrollTester)
	scrollTester = null
	return barWidth
}
function db(fn, wait = 50) {
	let timer = null
	return function (...args) {
		if (timer) clearTimeout(timer)
		timer = setTimeout(() => {
			fn.apply(this, args)

			clearTimeout(timer)
			timer = null
		}, wait)
	}
}
function adjust() {
	db(() => {
		// setBottomLine()
		// setNodata()
		setFix()
	}, 200)()
}
watch(
	() => props.data,
	() => {
		nextTick(() => {
			setFix()
		})
	},
	{ immediate: true, deep: true }
)
function setFix() {
	nextTick(() => {
		setFixDiv()
		syncThWidth()
		syncTrHeight()
	})
	// setTimeout(() => {
	// 	// if (this.type === 'v') {
	// 	// 	return
	// 	// }
	// 	setFixDiv()
	// 	syncThWidth()
	// 	syncTrHeight()
	// }, 0)
}
function hasVScroll() {
	return bodyTable.value.offsetHeight > $el.value.offsetHeight
}
function hasHScroll() {
	return bodyTable.value.offsetWidth > $el.value.offsetWidth
}
function setFixDiv() {
	if (Number(props.fixTop) > 0 && top.value && topTable.value) {
		if (hasVScroll()) {
			top.value.style.width = body.value.offsetWidth - getBarWidth() + 'px'
		}
		topTable.value.style.width = bodyTable.value.offsetWidth + 'px'
		bodyTable.value.style.width = bodyTable.value.offsetWidth + 'px'
	}
	if (Number(props.fixLeft) > 0 && left.value && $el.value) {
		if (hasHScroll()) {
			left.value.style.height = $el.value.offsetHeight - getBarWidth() + 'px'
		}
	}
	if (Number(props.fixRight) > 0 && right.value && $el.value) {
		if (hasHScroll()) {
			right.value.style.height = $el.value.offsetHeight - getBarWidth() + 'px'
		}
		if (hasVScroll()) {
			right.value.style.right = getBarWidth() + 'px'
			if (Number(props.fixTop) > 0) {
				rightTop.value.style.right = getBarWidth() + 'px'
			}
		}
	}
}

function syncThWidth() {
	if (props.columns.length > 0 && bodyHead.value && bodyHead.value.row) {
		const trs = bodyHead.value.row.children
		if (Number(props.fixTop) > 0 && topHead.value && topHead.value.row) {
			const topTrs = topHead.value.row.children
			for (let i = 0; i < topTrs.length; i++) {
				const ths = trs[i]?.children
				const topThs = topTrs[i]?.children
				if (ths && topThs) {
					for (let j = 0; j < ths.length; j++) {
						const th = ths[j]
						const topTh = topThs[j]
						if (th && topTh) {
							topTh.style.width = th.getBoundingClientRect().width + 'px'
						}
					}
				}
			}
		}
		if (Number(props.fixLeft) > 0 && leftHead.value && leftHead.value.row) {
			const leftTrs = leftHead.value.row.children
			for (let i = 0; i < leftTrs.length; i++) {
				const ths = trs[i]?.children
				const leftThs = leftTrs[i]?.children
				if (ths && leftThs && leftThs.length > 0) {
					for (let j = 0; j < leftThs.length; j++) {
						const th = ths[j]
						const leftTh = leftThs[j]
						if (th && leftTh) {
							leftTh.style.width = th.getBoundingClientRect().width + 'px'
							leftTh.style.height = th.getBoundingClientRect().height + 'px'
						}
					}
				}
			}
			if (Number(props.fixTop) > 0 && leftTopHead.value && leftTopHead.value.row) {
				const leftTopTrs = leftTopHead.value.row.children
				for (let i = 0; i < leftTopTrs.length; i++) {
					const ths = trs[i]?.children
					const leftTopThs = leftTopTrs[i]?.children
					if (ths && leftTopThs && leftTopThs.length > 0) {
						for (let j = 0; j < leftTopThs.length; j++) {
							const th = ths[j]
							const leftTopTh = leftTopThs[j]
							if (th && leftTopTh) {
								leftTopTh.style.width = th.getBoundingClientRect().width + 'px'
								leftTopTh.style.height = th.getBoundingClientRect().height + 'px'
							}
						}
					}
				}
			}
		}
		if (Number(props.fixRight) > 0 && rightHead.value && rightHead.value.row) {
			const rightTrs = rightHead.value.row.children
			for (let i = 0; i < rightTrs.length; i++) {
				const ths = trs[i]?.children
				const rightThs = rightTrs[i]?.children
				if (ths && rightThs && rightThs.length > 0) {
					for (let j = ths.length - rightThs.length; j < ths.length; j++) {
						const th = ths[j]
						const rightTh = rightThs[j - ths.length + rightThs.length]
						if (th && rightTh) {
							rightTh.style.width = th.getBoundingClientRect().width + 'px'
							rightTh.style.height = th.getBoundingClientRect().height + 'px'
						}
					}
				}
			}
			if (Number(props.fixTop) > 0 && rightTopHead.value && rightTopHead.value.row) {
				const rightTopTrs = rightTopHead.value.row.children
				for (let i = 0; i < rightTopTrs.length; i++) {
					const ths = trs[i]?.children
					const rightTopThs = rightTopTrs[i]?.children
					if (ths && rightTopThs && rightTopThs.length > 0) {
						for (let j = ths.length - rightTopThs.length; j < ths.length; j++) {
							const th = ths[j]
							const rightTopTh = rightTopThs[j - ths.length + rightTopThs.length]
							if (th && rightTopTh) {
								rightTopTh.style.width = th.getBoundingClientRect().width + 'px'
								rightTopTh.style.height = th.getBoundingClientRect().height + 'px'
							}
						}
					}
				}
			}
		}
	}
}
function syncTrHeight() {
	// if (this.type === 'v' || !props.syncHeight) { return }
	if (!(props.fixRight || props.fixLeft)) return
	if (bodyBody.value?.$el.children.length) {
		for (let i = 0; i < bodyBody.value.$el.children.length; i++) {
			const tr = bodyBody.value.$el.children[i]
			if (Number(props.fixRight) > 0 && rightBody.value) {
				rightBody.value.$el.children[i].style.height = tr.getBoundingClientRect().height + 'px'
			}
			if (Number(props.fixLeft) > 0 && leftBody.value) {
				leftBody.value.$el.children[i].style.height = tr.getBoundingClientRect().height + 'px'
			}
		}
	}
}

function scrollHandle() {
	var scrollLeft = body.value.scrollLeft
	var scrollTop = body.value.scrollTop
	if (typeof scrollLeft !== 'undefined') {
		if (props.fixTop && topTable.value) {
			topTable.value.style.left = 0 - scrollLeft + 'px'
		}
		if (props.fixRight && rightTable.value) {
			rightTable.value.style.top = 0 - scrollTop + 'px'
		}
		if (props.fixLeft && leftTable.value) {
			leftTable.value.style.top = 0 - scrollTop + 'px'
		}
	}
}

onMounted(() => {
	adjust()
})

// 选择支持
const hasRadio = computed(() => {
	let flag = false
	props.columns.map(item => {
		if (item.type === 'radio') {
			flag = true
		}
	})
	return flag
})

// 排序支持
function sortChange(sortInfo) {
	emit('sort', sortInfo)
}
const hasCheck = computed(() => {
	let flag = false
	props.columns.map(item => {
		if (item.type === 'check') {
			flag = true
		}
	})
	return flag
})
function change(rows, checked, isAll) {
	if (rows) {
		//type=check
		let ids: modelType = ''
		if (Array.isArray(rows)) {
			ids = rows.map(item => item[props.valueKeys])
		} else {
			ids = rows[props.valueKeys]
		}
		emit('update:modelValue', ids)
	} else {
		emit('update:modelValue', '')
	}
	emit('change', rows, checked, isAll)
	checkedAll.value = isAll
}
function checkedAllChange(isAll) {
	if (isAll) {
		let ids = props.data.map(item => item[props.valueKeys])
		emit('update:modelValue', ids)
		emit('change', props.data, true, true)
	} else {
		emit('update:modelValue', [])
		emit('change', [], false, false)
	}
	bodyBody.value.checkAll(isAll)
	if (Number(props.fixLeft) > 0) {
		leftBody.value.checkAll(isAll)
	}
	if (Number(props.fixRight) > 0) {
		rightBody.value.checkAll(isAll)
	}
}

// todo 默认全选的需求

// 多级表头支持
const flattenColumns = computed(() => {
	// 打平多级 columns 数组，用于 slot 遍历
	let arr = []
	function setArr(data) {
		data.map(column => {
			let tempColumn = JSON.parse(JSON.stringify(column))
			if (tempColumn.children) {
				delete tempColumn.children
			}
			arr.push(tempColumn)
			if (column.children) {
				setArr(column.children)
			}
		})
	}
	setArr(props.columns)
	return arr
})

function getColWidth(column) {
	if (column.width) {
		if (isNaN(Number(column.width))) {
			return column.width
		} else {
			return column.width + 'em'
		}
	}
}
const handleTheadCellClick = (column, type) => {
	if (props.sortFunction) {
		props.sortFunction(column, type)
	}
}
const hanldeDetailChange = (column, index, value) => {
	emit('detailChange', column, index, value)
}
export interface TableProvideType {
	handleTheadCellClick: (column, type) => void
	hanldeDetailChange: (column, index, value) => void
}

provide<TableProvideType>('TableRef', {
	handleTheadCellClick,
	hanldeDetailChange
})
</script>
